import { FC, useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import { reportBusinessAsync } from '../../../data-layer/business/actions';
import { reportCollectionAsync } from '../../../data-layer/collections/actions';
import { reportPostAsync } from '../../../data-layer/posts/actions';
import { useDispatch } from '../../../data-layer/useDispatch';
import { Dialog, DialogContent, DialogTitle } from '../../../ui/feedback/Dialog';
import { Button } from '../../../ui/input/Button';
import { FormControlGroup } from '../../../ui/input/FormControlGroup';
import { FormControlLabel, LabelPlacement } from '../../../ui/input/FormControlLabel';
import { Radio } from '../../../ui/input/Radio';
import { Box } from '../../../ui/layout/Box';
import { DataTestId } from '../../../ui/layout/DataTestIdWrapper';
import { Flex } from '../../../ui/layout/Flex';
import { Typography } from '../../../ui/typography/Typography';
import { useBackButton } from '../../../utils/useBackButton';
import { useTranslation } from '../../../utils/useTranslation';
import { openIntellectualPropertyReport } from '../../utils/openIntellectualPropertyReport';
import { REPORT_REASONS_OPTIONS } from './constants';
import { FormValue, ReportReasons } from './types';

interface ReportDialogProps {
    onClose?: VoidFunction;
    targetId?: string;
    targetType?: string;
    onReportSuccess?: VoidFunction;
}

export const ReportDialog: FC<ReportDialogProps> = ({
    onClose,
    targetId,
    targetType,
    onReportSuccess,
}) => {
    const onNavigateBack = useBackButton();
    const t = useTranslation();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const reportTargetId = targetId || state.targetId;
    const reportTargetType = targetType || state.targetType;

    const onReport = useCallback(
        (formValue: FormValue) => {
            if (formValue.reason === ReportReasons.INTELLECTUAL_PROPERTY_VIOLATION) {
                openIntellectualPropertyReport();
                onNavigateBack();

                return;
            }

            if (reportTargetType === 'post' || reportTargetType === 'blogger_post') {
                dispatch(reportPostAsync(reportTargetId, formValue.reason));
            }
            if (reportTargetType === 'business') {
                dispatch(reportBusinessAsync(reportTargetId, formValue.reason));
            }
            if (reportTargetType === 'collection') {
                dispatch(reportCollectionAsync(reportTargetId, formValue.reason));
            }

            if (onReportSuccess) {
                onReportSuccess();
            } else {
                onNavigateBack();
                // После отправки репорта очищаем последний стейт,
                // чтобы кнопкой Вперед нельзя было открыть репорт на этот же пост INSPIRE-5644
                window.history.replaceState({}, '', '');
            }
        },
        [reportTargetType, onReportSuccess, onNavigateBack, dispatch, reportTargetId],
    );

    return (
        <Dialog
            onClose={onClose || onNavigateBack}
            borderRadius={{ xs: 0, sm: 3 }}
            maxWidth={{ xs: '100%', sm: 512 }}
        >
            <DialogTitle onClose={onClose || onNavigateBack}>
                <Typography variant="title3" weight="medium">
                    {t('Пожаловаться')}
                </Typography>
            </DialogTitle>
            <DialogContent py={0} px={2}>
                <Flex width="100%">
                    <Box py={2}>
                        <Typography variant="title2" weight="bold">
                            {t('Расскажите, что не так')}
                        </Typography>
                    </Box>

                    <Form
                        onSubmit={onReport}
                        initialValues={{}}
                        render={({
                            submitting,
                            pristine,
                            invalid,
                            submitError,
                            handleSubmit,
                            values,
                        }) => (
                            <Flex as="form" width="100%" onSubmit={handleSubmit}>
                                <Field
                                    name="reason"
                                    required
                                    render={({ input }) => (
                                        <FormControlGroup width="100%" {...input}>
                                            {REPORT_REASONS_OPTIONS.map((option) => (
                                                <Box
                                                    key={option.value}
                                                    width="100%"
                                                    borderBottom="2px solid var(--divider)"
                                                    onClick={() => input.onChange(option.value)}
                                                    dataTestId={
                                                        option.value === ReportReasons.OTHER
                                                            ? DataTestId.REPORT_REASON_OTHER
                                                            : undefined
                                                    }
                                                >
                                                    <FormControlLabel
                                                        label={t(option.label)}
                                                        labelPlacement={LabelPlacement.LEFT}
                                                        control={<Radio value={option.value} />}
                                                    />
                                                </Box>
                                            ))}
                                        </FormControlGroup>
                                    )}
                                />

                                <Flex width="100%" py={2}>
                                    <Box mt="auto" width="100%">
                                        {submitError ? (
                                            <Typography variant="caption" color="error" mb={2}>
                                                {submitError}
                                            </Typography>
                                        ) : null}
                                        <Button
                                            size="l"
                                            width="100%"
                                            type="submit"
                                            disabled={pristine || submitting || invalid}
                                            dataTestId={DataTestId.SEND_REPORT_BUTTON}
                                        >
                                            {values?.reason ===
                                            ReportReasons.INTELLECTUAL_PROPERTY_VIOLATION
                                                ? t('Заполнить форму')
                                                : t('Отправить')}
                                        </Button>
                                    </Box>
                                </Flex>
                            </Flex>
                        )}
                    />
                </Flex>
            </DialogContent>
        </Dialog>
    );
};
